<template>
  <div>
    <static-alert :message="message" @on-hide="message=''" />

    <!-- Dashboard Header with Controls -->
    <div class="mb-3">

      <!-- Stats Cards Row -->
      <div class="row g-3 mb-3">
        <div class="col-lg-4 col-sm-6">
          <stat-card
              @action="$router.push({name:'InsightsSpeed'})"
              :stat="{label:'Orders', value: totalOrderCount, actionLabel: `${totalItemsCount} items`, comparison: {mode: comparisonMode, current: totalOrderCount, previous: comparisonTotalOrderCount, format:'int', label: comparisonLabel, showLabel: true}}"
          />
        </div>
        <div class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">Sales</div>
            <div class="stat-value"><money-block :dollar="grossSalesValue" /></div>
            <em class="small text-muted"><money-block :dollar="grossOrderValue" class="d-inline-block" /> gross revenue</em>
            <range-comparison v-if="comparisonMode && !comparisonFetching" :curr-val="grossSalesValue" :prev-val="comparisonGrossSalesValue" format="dollar" :label="comparisonLabel" />
            <div class="stat-icon text-secondary"><i class="bi bi-coin"></i></div>
          </div>
        </div>
        <div class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">Profit</div>
            <div class="stat-value"><money-block :dollar="totalOrderValue" /></div>
            <em class="small text-muted" title="Includes seller & promotion fees, shipping, and COGS">After all costs</em>
            <range-comparison v-if="comparisonMode && !comparisonFetching" :curr-val="totalOrderValue" :prev-val="comparisonTotalOrderValue" format="dollar" :label="comparisonLabel" />
            <div class="stat-icon text-secondary"><i class="bi bi-currency-dollar"></i></div>
          </div>
        </div>
        <div class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">Net Average</div>
            <div class="stat-value"><money-block :dollar="totalOrderCount ? totalOrderValue / totalOrderCount : 0" /></div>
            <em class="small text-muted">Your overall profit per item</em>
            <range-comparison v-if="comparisonMode && !comparisonFetching" :curr-val="totalOrderCount ? totalOrderValue / totalOrderCount : 0" :prev-val="comparisonNetAverage" format="dollar" />
            <div class="stat-icon text-secondary"><i class="bi bi-graph-up"></i></div>
          </div>
        </div>
        <div class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">Net Median</div>
            <div class="stat-value"><money-block :dollar="median" /></div>
            <em class="small text-muted">Best / Worst: ${{ rangeLow.toFixed(2) }} <span class="mx-1 bi bi-arrow-right"></span> ${{ rangeHigh.toFixed(2) }}</em>
            <range-comparison v-if="comparisonMode && !comparisonFetching" :curr-val="parseFloat(median)" :prev-val="parseFloat(comparisonMedian)" format="dollar" :label="comparisonLabel" />
            <div class="stat-icon text-secondary"><i class="bi bi-circle-half"></i></div>
          </div>
        </div>
        <div class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">ROI</div>
            <div class="stat-value" v-if="ROI">
              {{ ROI.toFixed(1) }}%
            </div>
            <div class="stat-value" v-if="!ROI">-</div>
            <em class="text-muted small">ROI excludes free / $0-cost items</em>
            <range-comparison v-if="comparisonMode && !comparisonFetching && ROI && comparisonROI" :curr-val="ROI" :prev-val="comparisonROI" format="percent" :label="comparisonLabel" />
            <div class="stat-icon text-secondary"><i class="bi bi-percent"></i></div>
          </div>
        </div>
        <div v-if="false" class="col-lg-4 col-sm-6">
          <div class="stat-card">
            <div class="stat-label">Net Range</div>
            <div class="stat-value stat-wide">
              <money-block :dollar="rangeLow" />
              <span class="mx-1 bi bi-arrow-right"></span>
              <money-block :dollar="rangeHigh" />
            </div>
            <div class="stat-icon text-secondary"><i class="bi bi-arrows"></i></div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <transition mode="out-in">
      <div class="row g-3" v-if="!topTen && !flipFlop">
        <cross-listing-gate>
          <div class="col-md-4">
            <div class="chart-card">
              <h6 class="chart-title">Profit by Platform</h6>
              <apexchart
                v-if="platformChartData.series.length > 0"
                type="donut"
                height="250"
                :options="platformChartData.options"
                :series="platformChartData.series"
              />
              <div v-else class="text-muted text-center py-5">No data</div>
            </div>
          </div>
        </cross-listing-gate>

        <div :class="crosslisting ? 'col-md-8' : 'col-md-12'">
          <div class="chart-card">
            <div class="d-flex align-items-center mb-3">
              <h6 class="chart-title p-0 m-0 me-2">Profit by {{ categoryMetric === 'ebay' ? 'Listing' : 'Source' }} Category</h6>
              <div class="ms-auto d-flex align-items-center justify-content-center text-muted" style="font-size: 0.7rem; line-height: 0.7;">
                <span>by: </span>
                <button class="btn btn-link btn-sm p-0 m-0" style="font-size: inherit; line-height: inherit;" :class="{'fw-bold': categoryMetric === 'source'}" @click="categoryMetric = 'source'">source</button>
                <span class="mx-1">|</span>
                <button class="btn btn-link btn-sm p-0 m-0" style="font-size: inherit; line-height: inherit;" :class="{'fw-bold': categoryMetric === 'ebay'}" @click="categoryMetric = 'ebay'">listing</button>
              </div>
            </div>
            <div>
              <div v-if="categoryMetric === 'source'">
                <apexchart
                    v-if="sourceCategoryChartData.series.length > 0"
                    type="bar"
                    height="250"
                    :options="sourceCategoryChartData.options"
                    :series="sourceCategoryChartData.series"
                />
                <div v-else class="text-muted text-center py-5">No data</div>
              </div>
              <div v-if="categoryMetric === 'ebay'">
                <div v-if="ebayCategoryChartData.series.length > 0">
                  <apexchart
                      type="bar"
                      :height="Math.max(200, (chartData.ebay_category_sales || []).filter(c => c.sold_items > 0).length * 36)"
                      :options="ebayCategoryChartData.options"
                      :series="ebayCategoryChartData.series"
                  />
                  <div class="d-none d-flex align-items-center justify-content-center text-muted" style="font-size: 0.7rem; line-height: 0.7;">
                    <span class="ms-auto">by: </span>
                    <button class="btn btn-link btn-sm p-0 m-0" style="font-size: inherit; line-height: inherit;" :class="{'fw-bold': ebayCategoryMetric === 'sales'}" @click="ebayCategoryMetric = 'sales'">net profit</button>
                    <span class="mx-1">|</span>
                    <button class="btn btn-link btn-sm p-0 m-0" style="font-size: inherit; line-height: inherit;" :class="{'fw-bold': ebayCategoryMetric === 'items'}" @click="ebayCategoryMetric = 'items'">items</button>
                  </div>
                </div>

                <div v-else class="text-muted text-center py-4">No data</div>
              </div>
            </div>
          </div>
        </div>

      </div>
      </transition>

    </div>

    <div class="h-page">
      <transition mode="out-in">
        <div v-if="fetching" class="h-page d-flex justify-content-center align-items-center">
          <loading-spinner  />
        </div>
        <div v-else-if="orderList.length > 0">

          <div class="card bg-light-subtle shadow mb-4">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <h3>{{ topSalesTitle }}</h3>
                <button type="button" class="btn btn-link btn-sm ms-2"  @click="startPresentation('top10')">
                  <i class="bi bi-camera-fill"></i> Slideshow
                </button>
              </div>
              <div class="grid-container">
                <div v-for="(order, idx) in top10List" :key="order.order_id">
                  <order-card class="mx-auto" :order-id="order.order_id" @order-changed="getChartData" :callback="() => startPresentation('top10', idx)" />
                </div>
              </div>
            </div>
          </div>

          <div v-if="orderList.length > 10" class=" mb-4">
            <div class="d-flex align-items-center mb-3">
              <h4 class="text-muted">Other Sales</h4>
              <button v-if="orderList.length > 1"  type="button" class="btn btn-link btn-sm ms-2"  @click="startPresentation('flipFlop')" title=" Lowest profit sale ">
                <i class="bi bi-camera-fill"></i> Flip Flop
              </button>
            </div>
            <div class="grid-container">
              <div v-for="(order, idx) in pageList" :key="order.order_id" >
                <order-card class="mx-auto" :order-id="order.order_id" @order-changed="getChartData" :callback="() => startPresentation(null, idx + 10)" />
              </div>
            </div>
          </div>

          <app-modal modal-class="modal-xl" :modal-in="!!presentationMode" @close="stopPresentation" >
            <template #header>
              <div class="me-auto d-flex align-items-center mb-3 mb-md-0">
                <span class="me-3" style="white-space: nowrap"><i class="bi bi-camera-fill me-2"></i> {{ modalTitle }}</span>
              </div>
            </template>
            <div v-if="modalList.length > 1" class="d-flex mb-3 align-items-center top-ten-nav">
              <button class="btn btn-light btn-outline-primary me-auto" @click="nextOrder(-1)">
                <i class="bi bi-chevron-left" title="Previous Order"></i>
              </button>
              <h2 class="mx-3 text-muted">{{ presentationMode === 'flipFlop' ? 'Flip Flop' : `# ${currentOrder + 1 }` }}</h2>
              <button class="btn btn-light btn-outline-primary ms-auto" @click="nextOrder(1)">
                <i class="bi bi-chevron-right" title="Next Order"></i>
              </button>
            </div>
            <transition mode="out-in">
              <div class="container">
                <platform-order :order-id="modalList[currentOrder].order_id" @order-changed="getChartData" :key="currentOrder" :discreet="true" />
              </div>
            </transition>
          </app-modal>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, defineProps, watch, defineAsyncComponent, onMounted} from "vue"
import { useStore } from "vuex"

import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import StaticAlert from "@/app/components/Utils/StaticAlert.vue";
import PlatformOrder from "@/app/components/ebay/PlatformOrder.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import OrderCard from "@/app/components/orders/OrderCard.vue";
import RangeComparison from "@/app/components/Utils/RangeComparison.vue";


// ApexCharts component
const apexchart = defineAsyncComponent(() =>
  import('vue3-apexcharts').then(module => module.default || module)
);

const props = defineProps({
  range: {
    type: Object,
  }
})

import {DateUtil} from "@/services/dateUtils";
import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";
import StatCard from "@/app/components/Utils/StatCard.vue";
const storeConfig = computed(() => store.state.eshop.eshop)
const dateUtil = computed(() => new DateUtil(storeConfig.value?.timezone || store.state.defaultTimezone))

const store = useStore()

const CHART_COLORS = computed(() => store.state.chartColors)
const orderList = ref([])
const fetching = ref(false)
const message = ref('')
const currentOrder = ref(0)
const presentationMode = ref(false)
const topTen = ref(false)
const flipFlop = ref(false)
const modalTitle = ref('Order Gallery')

const comparisonMode = computed(() => props.range?.comparison || 'prev')
const comparisonLabel = computed(() => comparisonMode.value === 'yoy' ? 'vs. last year' : 'vs. prev period')
const comparisonOrderList = ref([])
const comparisonFetching = ref(false)

const ebayCategoryMetric = ref('sales')
const categoryMetric = ref('source')

  function getComparisonDates() {
    const from = props.range.from
    const to = props.range.to
    if (comparisonMode.value === 'yoy') {
      return {
        from: from?.minus ? from.minus({ years: 1 }) : new Date(new Date(from).setFullYear(new Date(from).getFullYear() - 1)),
        to: to?.minus ? to.minus({ years: 1 }) : new Date(new Date(to).setFullYear(new Date(to).getFullYear() - 1))
      }
    }
    // 'prev' — shift back by range span for non-overlapping periods
    const spanDays = from?.diff
      ? Math.floor(to.startOf('day').diff(from.startOf('day'), 'days').days) + 1
      : Math.floor((new Date(to).setHours(0,0,0,0) - new Date(from).setHours(0,0,0,0)) / 86400000) + 1
    return {
      from: from?.minus ? from.minus({ days: spanDays }) : new Date(new Date(from).getTime() - spanDays * 86400000),
      to: to?.minus ? to.minus({ days: spanDays }) : new Date(new Date(to).getTime() - spanDays * 86400000)
    }
  }

  async function getComparisonOrders() {
    comparisonFetching.value = true
    const { from, to } = getComparisonDates()
    const fromDate = dateUtil.value.toISODate(from)
    const toDate = dateUtil.value.toISODate(to)
    store.state.api.get('/orders/orders-by-date-range', { params: { fdate: fromDate, tdate: toDate } })
      .then(r => r.data).then(d => d.orders)
      .then(orders => { comparisonOrderList.value = orders })
      .finally(() => { comparisonFetching.value = false })
  }

  const comparisonTotalOrderCount = computed(() => comparisonOrderList.value.length || 0)
  const comparisonTotalOrderValue = computed(() => comparisonOrderList.value.reduce((acc, item) => acc + parseFloat(item.net), 0.0))
  const comparisonGrossSalesValue = computed(() => comparisonOrderList.value.reduce((acc, item) => acc + parseFloat(item.sales), 0.0))
  const comparisonNetAverage = computed(() => comparisonTotalOrderCount.value ? comparisonTotalOrderValue.value / comparisonTotalOrderCount.value : 0)
  const comparisonMedian = computed(() => {
    const ol = comparisonOrderList.value
    if (ol.length === 0) return 0
    if (ol.length === 1) return ol[0].net
    if (ol.length % 2 === 0) return (parseFloat(ol[ol.length/2-1].net) + parseFloat(ol[ol.length/2].net)) / 2
    return ol[Math.floor(ol.length/2)].net
  })
  const comparisonSourcingCosts = computed(() => comparisonOrderList.value.reduce((sum, obj) => sum - parseFloat(obj.cogs || 0), 0))
  const comparisonNetWithCosts = computed(() => comparisonOrderList.value.reduce((sum, obj) => {
    const cogs = parseFloat(obj.cogs || 0)
    return sum + (cogs !== 0 ? parseFloat(obj.net) : 0)
  }, 0))
  const comparisonROI = computed(() => {
    if (!comparisonSourcingCosts.value || comparisonSourcingCosts.value === 0 || comparisonNetWithCosts.value === 0) return null
    return parseFloat(comparisonNetWithCosts.value / comparisonSourcingCosts.value * 100)
  })

  const chartData = ref({
    platform_sales: [],
    category_sales: [],
    top_items: []
  });

  const crosslisting = computed(() => store.state.eshop.eshop?.config?.CROSSLISTING)

  const pageList = computed(() => {
    const ol = orderList.value //.filter(o => o.order_status==='Completed')
    // return ol.slice(flipFlop.value ? -1 : 0, topTen.value ? 10 : ol.length)
    return ol.slice(10, ol.length)
  })

  const totalOrderCount = computed(() => orderList.value.length || 0)
  const totalItemsCount = computed(() => orderList.value.reduce((acc, item) => acc + parseInt(item.items), 0))
  const totalOrderValue = computed(() => orderList.value.reduce((acc, item) => acc + parseFloat(item.net), 0.0))
  const grossOrderValue = computed(() => orderList.value.reduce((acc, item) => acc + parseFloat(item.gross), 0.0))
  const grossSalesValue = computed(() => orderList.value.reduce((acc, item) => acc + parseFloat(item.sales), 0.0))
  const sourcingCosts = computed(() => {
    return orderList.value.reduce((sum, obj) => sum - parseFloat(obj.cogs || 0), 0)
  })
  const netWithCosts = computed(() => {
    return orderList.value.reduce((sum, obj) => {
      const cogs = parseFloat(obj.cogs || 0)
      return sum + (cogs !== 0 ? parseFloat(obj.net) : 0);
    }, 0)
  })
  const ROI = computed(() => {
    if (!sourcingCosts.value || sourcingCosts.value  === 0 || netWithCosts.value === 0 ) return null
    return parseFloat(netWithCosts.value / sourcingCosts.value * 100)
  })
  const rangeLow = computed(() => {
    if (orderList.value.length === 0) return 0
    // Since pageList is sorted descending by net, lowest is last
    return parseFloat(orderList.value[orderList.value.length - 1].net)
  })
  const rangeHigh = computed(() => {
    if (orderList.value.length === 0) return 0
    // Since pageList is sorted descending by net, highest is first
    return parseFloat(orderList.value[0].net)
  })
  const median = computed( () => {
    if (orderList.value.length === 0) return 0
    if (orderList.value.length === 1) return orderList.value[0].net
    if (orderList.value.length % 2 === 0) {
      return (parseFloat(orderList.value[orderList.value.length/2-1].net) + parseFloat(orderList.value[orderList.value.length/2].net)) / 2
    }
    return orderList.value[Math.floor(orderList.value.length/2)].net
  })

const topSalesTitle = computed(() => {
  const week = (presentationMode.value === 'top10' && props.range.preset.toLowerCase().search(/week/) !== -1) ? ` (Week ${ dateUtil.value.getWeekNumber(props.range.from) })` : ''
  if (orderList.value.length >= 10) return `Top 10 Sales ${week}`
  if (orderList.value.length === 1) return `Top Sale ${week}`
  return `Top Sales ${week}`;
})

const top10List = computed(() => orderList.value.slice(0,10))
const flipFlopList = computed(() => orderList.value.slice(-1, orderList.value.length))
const modalList = computed(() => {
  if (presentationMode.value === 'top10') return top10List.value
  if (presentationMode.value === 'flipFlop') return flipFlopList.value
  return orderList.value
})

  // Chart Data: Sales by Platform
  const platformChartData = computed(() => {
    const data = chartData.value.platform_sales || []
    const labels = data.map(item => item.platform)
    const series = data.map(item => item.net_sales)

    return {
      series,
      options: {
        chart: { type: 'donut' },
        labels,
        legend: { position: 'bottom' },
        colors: Object.values(CHART_COLORS.value), // [CHART_COLORS.value.success, CHART_COLORS.value.teal, CHART_COLORS.value.warning, CHART_COLORS.value.danger, CHART_COLORS.value.gray600],
        dataLabels: {
          enabled: true,
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 2,
            color: '#000000',
            opacity: 0.8
          },
          formatter: (val) => `${val.toFixed(1)}%`
        },
        tooltip: {
          y: {
            formatter: (val) => `$${val.toFixed(2)}`
          }
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                value: {
                  formatter: (val) => `$${parseFloat(val).toFixed(2)}`
                },
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => `$${series.reduce((a, b) => a + b, 0).toFixed(2)}`
                }
              }
            }
          }
        }
      }
    }
  })

  // Chart Data: Sales by Source Category
  const sourceCategoryChartData = computed(() => {
    const data = chartData.value.category_sales || []

    const total = data
        .reduce((t, c) => t + c.net_sales, 0)

    // Limit to top 12 categories, sorted by net_sales
    const topCategories = data
      .sort((a, b) => b.net_sales - a.net_sales)
      .slice(0, 12)

    const categories = topCategories.map(item => {
      const maxLength = 20
      return item.category.length > maxLength
        ? item.category.substring(0, maxLength) + '...'
        : item.category
    })

    const series = [{
      name: 'Net Profit',
      data: topCategories.map(item => item.net_sales)
    }]

    return {
      series,
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          animations: { enabled: true }
        },
        plotOptions: {
          bar: {
            barHeight: '95%',
            borderRadius: 2,
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          offsetX: 15,
          style: {
            fontSize: '12px',
            colors: ['#000']
          },
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 2,
            color: '#fff',
            opacity: 1
          },
          // formatter: (val, opt) => `${opt.w.globals.labels[opt.dataPointIndex]}: $${val.toFixed(2)} (${Math.round(parseFloat(val)/total*100)}%)`,
          formatter: (val) => `$${val.toFixed(2)} (${Math.round(parseFloat(val)/total*100)}%)`
        },
        xaxis: {
          categories,
          labels: {
            formatter: (val) => `$${Math.round(parseFloat(val))}`
          }
        },
        yaxis: {
          labels: {
            //show: false,
            style: {
              fontSize: '11px'
            }
          }
        },
        colors: Object.values(CHART_COLORS.value), // [CHART_COLORS.value.info, CHART_COLORS.value.secondary, CHART_COLORS.value.mint, CHART_COLORS.value.goldenrod, CHART_COLORS.value.teal, CHART_COLORS.value.deepTeal, CHART_COLORS.value.warning, CHART_COLORS.value.danger],
        legend: { show: false },
        tooltip: {
          y: {
            formatter: (val) => `$${val.toFixed(2)}`
          }
        },
        responsive: [{ breakpoint: 450, options: { xaxis: { tickAmount: 3 } } }]
      }
    }
  })

const ebayCategoryChartData = computed(() => {
  const isSales = ebayCategoryMetric.value === 'sales'
  const total = (chartData.value.ebay_category_sales || [])
      .reduce((t, c) => t + c.net_sales, 0)

  const data = (chartData.value.ebay_category_sales || [])
    .filter(c => c.sold_items > 0)
    .sort((a, b) => isSales ? b.net_sales - a.net_sales : b.sold_items - a.sold_items)

  const categories = data.map(item => item.main_category_name)
  const series = [{
    name: isSales ? 'Net Profit' : 'Items Sold',
    data: isSales
      ? data.map(item => parseFloat(item.net_sales.toFixed(2)))
      : data.map(item => item.sold_items)
  }]

  return {
    series,
    options: {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { barHeight: '95%', borderRadius: 2, horizontal: true, distributed: true, dataLabels: {position: 'top'} } },
      dataLabels: {
        enabled: true,
        textAnchor: 'start',
        offsetX: 15,
        style: {
          fontSize: '12px',
          colors: ['#000']
        },
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 2,
          color: '#fff',
          opacity: 1
        },
        // formatter: (val, opt) => {
        //   if (isSales) {
        //     return `${opt.w.globals.labels[opt.dataPointIndex]}: $${val.toFixed(2)} (${Math.round(parseFloat(val)/total*100)}%)`
        //   }
        //   return val
        // }
        formatter: (val) => isSales ? `$${val.toFixed(2)} (${Math.round(parseFloat(val)/total*100)}%)` : `${val}`
      },
      xaxis: {
        categories,
        labels: { formatter: (val) => isSales ? `$${Math.round(parseFloat(val))}` : `${val}` }
      },
      yaxis: { labels: { style: { fontSize: '11px' }, maxWidth: 160 } },
      colors: Object.values(CHART_COLORS.value), // [CHART_COLORS.value.info, CHART_COLORS.value.secondary, CHART_COLORS.value.mint, CHART_COLORS.value.goldenrod, CHART_COLORS.value.teal, CHART_COLORS.value.deepTeal, CHART_COLORS.value.warning, CHART_COLORS.value.danger],
      legend: { show: false },
      tooltip: { y: { formatter: (val) => isSales ? `$${val.toFixed(2)}` : `${val} items` } },
      responsive: [{ breakpoint: 450, options: { xaxis: { tickAmount: 3 } } }]
    }
  }
})

  onMounted(() => {
    getOrders()
    getChartData()
    getComparisonOrders()
  })

 async function getOrders() {
   fetching.value = true
   const fromDate = dateUtil.value.toISODate(props.range.from)
   const toDate = dateUtil.value.toISODate(props.range.to)
   store.state.api.get('/orders/orders-by-date-range', {params: {
       fdate: fromDate,
       tdate: toDate
     }})
       .then(response => response.data)
       .then(data => data.orders)
       .then(orders => {
         orderList.value = orders
         currentOrder.value = 0;
       })
       .finally(() => {
         fetching.value = false
       })


 }

  function getChartData() {

    const fromDate = dateUtil.value.toISODate(props.range.from)
    const toDate = dateUtil.value.toISODate(props.range.to)

    store.dispatch("eshop/getOrdersChartData", { fromDate, toDate })
        .then(data => {
          chartData.value = data
        })
        .catch(error => {
          console.error('Failed to load chart data:', error)
        })
  }

  function stopPresentation() {
    currentOrder.value = 0
    presentationMode.value = false
    modalTitle.value = 'Order Gallery'
  }

  function startPresentation(mode, idx=null) {
    presentationMode.value = mode || true
    if (mode === 'top10') {
      currentOrder.value = idx === null ? modalList.value.length - 1 : idx
      modalTitle.value = topSalesTitle.value
    } else if (mode === 'flipFlop') {
      currentOrder.value = idx || 0
      modalTitle.value = 'Flip Flop'
    } else {
      currentOrder.value = idx || 0
      modalTitle.value = 'Order Gallery'
    }
  }

  function nextOrder(dir) {
    let newVal = currentOrder.value + dir
    if (newVal < 0) newVal = modalList.value.length - 1
    if (newVal >= modalList.value.length) newVal = 0
    currentOrder.value = newVal
  }

  // function pseudoFetch(callback) {
  //   fetching.value = true
  //   if (typeof callback === 'function') callback()
  //   nextTick(() => fetching.value = false)
  // }

  watch(() => props.range, () => {
    onDateChange()
  }, { deep: true })


  function onDateChange(){
    // Fetch new chart data for the selected date range
    categoryMetric.value = 'source'
    getOrders()
    getChartData()
    getComparisonOrders()
    fetch('/api/events', {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify({
        event_type: 'date_range_changed',
        context: 'TopOrders',
        ts: Date.now()
      })
    });
  }
</script>

<style lang="scss" scoped>
  @import '@/assets/sass/_variables-esc.scss';

  .striped > div:nth-of-type(2n+1) {background: rgba(0,0,0,0.05);}
  .top-ten-nav h2 {font-size:4rem;}


</style>
